import { db } from "../../utils/firebase";
import { LoggerService } from "../logging/LoggerService";

export interface LockResult {
  acquired: boolean;
  reason?: "SUCCESS" | "ALREADY_PROCESSED" | "PROCESSING_IN_PROGRESS" | "LOCK_FAILED";
  existingRecord?: any;
  status?: string;
}

// Map of active promises in memory for single-node process deduplication
const activePromises = new Map<string, Promise<any>>();

// Fallback in-memory lock store for offline / test / fast-access mode with TTL tracking
const inMemoryLocks: Record<string, { status: string; lockedAt: string; processedAt?: string; record?: any; timestamp?: number }> = {};

// In-memory processed webhook event IDs with bounded size and timestamp
const inMemoryProcessedEvents = new Map<string, number>();
const MAX_PROCESSED_EVENTS_CACHE = 500;
const LOCK_RETENTION_TTL_MS = 15 * 60 * 1000; // 15 minutos em memória

export class FinancialLockService {
  /**
   * Cleanup expired in-memory locks to prevent unbounded memory growth
   */
  static cleanupExpiredLocks(maxAgeMs = LOCK_RETENTION_TTL_MS): number {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [key, lock] of Object.entries(inMemoryLocks)) {
      const lockTime = lock.timestamp || (lock.processedAt ? new Date(lock.processedAt).getTime() : new Date(lock.lockedAt).getTime());
      if (!isNaN(lockTime) && (now - lockTime > maxAgeMs)) {
        delete inMemoryLocks[key];
        cleanedCount++;
      }
    }

    // Limpar eventos de webhook antigos
    for (const [eventId, eventTime] of inMemoryProcessedEvents.entries()) {
      if (now - eventTime > maxAgeMs) {
        inMemoryProcessedEvents.delete(eventId);
      }
    }

    // Manter limite máximo de tamanho
    if (inMemoryProcessedEvents.size > MAX_PROCESSED_EVENTS_CACHE) {
      const excess = inMemoryProcessedEvents.size - MAX_PROCESSED_EVENTS_CACHE;
      let removed = 0;
      for (const eventId of inMemoryProcessedEvents.keys()) {
        if (removed >= excess) break;
        inMemoryProcessedEvents.delete(eventId);
        removed++;
      }
    }

    return cleanedCount;
  }

  /**
   * Check if a webhook event ID has already been processed (Event-level idempotency)
   */
  static isEventProcessed(eventId: string): boolean {
    if (!eventId || typeof eventId !== "string") return false;
    return inMemoryProcessedEvents.has(eventId.trim());
  }

  /**
   * Mark a webhook event ID as processed
   */
  static async markEventProcessed(eventId: string, adminDbInstance?: any): Promise<void> {
    if (!eventId || typeof eventId !== "string") return;
    const cleanEventId = eventId.trim();
    inMemoryProcessedEvents.set(cleanEventId, Date.now());

    // Se atingiu o limite máximo, executa limpeza rápida
    if (inMemoryProcessedEvents.size > MAX_PROCESSED_EVENTS_CACHE) {
      FinancialLockService.cleanupExpiredLocks();
    }

    const activeDb = adminDbInstance || (typeof db !== "undefined" ? db : null);
    if (activeDb) {
      try {
        if (typeof activeDb.collection === "function") {
          await activeDb.collection("webhook_events").doc(cleanEventId).set({
            processedAt: new Date().toISOString(),
            eventId: cleanEventId,
            expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            ttlTimestamp: Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000)
          }, { merge: true });
        }
      } catch (err: any) {
        LoggerService.warn(`[FinancialLockService] Warning saving processed event ${cleanEventId}:`, err.message || err);
      }
    }
  }
  /**
   * Acquire atomic lock for a given paymentId before executing financial payout or confirmation.
   * Lock timeout defaults to 60,000ms (60 seconds).
   */
  static async acquireLock(paymentId: string, adminDbInstance?: any): Promise<LockResult> {
    if (!paymentId || typeof paymentId !== "string") {
      return { acquired: false, reason: "LOCK_FAILED" };
    }

    const cleanPaymentId = paymentId.trim();
    const LOCK_TIMEOUT_MS = 60000; // 60s
    const now = new Date();
    const nowIso = now.toISOString();
    const nowTime = now.getTime();

    // 1. Check in-memory lock store
    const memLock = inMemoryLocks[cleanPaymentId];
    if (memLock) {
      if (memLock.status === "CONCLUIDO" || memLock.status === "PROCESSED") {
        return {
          acquired: false,
          reason: "ALREADY_PROCESSED",
          status: memLock.status,
          existingRecord: memLock.record
        };
      }
      if (memLock.status === "PROCESSING") {
        const lockedAtTime = new Date(memLock.lockedAt).getTime();
        if (nowTime - lockedAtTime < LOCK_TIMEOUT_MS) {
          return {
            acquired: false,
            reason: "PROCESSING_IN_PROGRESS",
            status: "PROCESSING",
            existingRecord: memLock.record
          };
        }
        // Lock expired -> allow takeover
      }
    }

    // 2. Atomic Firestore transaction check (if adminDbInstance or client db is available)
    const activeDb = adminDbInstance || (typeof db !== "undefined" ? db : null);

    if (activeDb) {
      try {
        if (adminDbInstance && typeof adminDbInstance.runTransaction === "function") {
          let txResult: LockResult | null = null;
          await adminDbInstance.runTransaction(async (transaction: any) => {
            const docRef = adminDbInstance.collection("repasses").doc(cleanPaymentId);
            const snap = await transaction.get(docRef);

            if (snap.exists) {
              const data = snap.data();
              const status = data.status;
              const lockedAt = data.lockedAt;
              const lockedAtTime = lockedAt ? new Date(lockedAt).getTime() : 0;

              if (status === "CONCLUIDO" || status === "PROCESSED") {
                txResult = {
                  acquired: false,
                  reason: "ALREADY_PROCESSED",
                  status,
                  existingRecord: data
                };
                return;
              }

              if (status === "PROCESSING") {
                if (nowTime - lockedAtTime < LOCK_TIMEOUT_MS) {
                  txResult = {
                    acquired: false,
                    reason: "PROCESSING_IN_PROGRESS",
                    status: "PROCESSING",
                    existingRecord: data
                  };
                  return;
                }
                // Lock expired -> takeover allowed
              }
            }

            // Lock acquired!
            transaction.set(docRef, {
              paymentId: cleanPaymentId,
              status: "PROCESSING",
              lockedAt: nowIso,
              updatedAt: nowIso
            }, { merge: true });

            txResult = { acquired: true, reason: "SUCCESS" };
          });

          if (txResult) {
            if ((txResult as LockResult).acquired) {
              inMemoryLocks[cleanPaymentId] = { status: "PROCESSING", lockedAt: nowIso };
            }
            return txResult;
          }
        }
      } catch (err: any) {
        LoggerService.warn(`[FinancialLockService] Warning during atomic lock transaction for ${cleanPaymentId}:`, err.message || err);
      }
    }

    // In-memory fallback acquisition
    inMemoryLocks[cleanPaymentId] = { status: "PROCESSING", lockedAt: nowIso };
    return { acquired: true, reason: "SUCCESS" };
  }

  /**
   * Helper to check if a paymentId is currently locked in processing state
   */
  static isLocked(paymentId: string): boolean {
    const lock = inMemoryLocks[paymentId.trim()];
    return Boolean(lock && lock.status === "PROCESSING");
  }

  /**
   * Release or update lock state after financial execution completes or fails.
   */
  static async releaseLock(
    paymentId: string,
    finalStatus: "CONCLUIDO" | "FALHO" | "PENDENTE" | "PROCESSED" | "FAILED" | string = "CONCLUIDO",
    record?: any,
    adminDbInstance?: any
  ): Promise<void> {
    const cleanPaymentId = paymentId.trim();
    const nowIso = new Date().toISOString();

    // Update in-memory lock store
    inMemoryLocks[cleanPaymentId] = {
      status: finalStatus,
      lockedAt: inMemoryLocks[cleanPaymentId]?.lockedAt || nowIso,
      processedAt: nowIso,
      record: record || inMemoryLocks[cleanPaymentId]?.record
    };

    // Update Firestore if available
    const activeDb = adminDbInstance || (typeof db !== "undefined" ? db : null);
    if (activeDb) {
      try {
        if (adminDbInstance && typeof adminDbInstance.collection === "function") {
          const docRef = adminDbInstance.collection("repasses").doc(cleanPaymentId);
          await docRef.set({
            status: finalStatus,
            processedAt: nowIso,
            updatedAt: nowIso,
            ...(record ? { repasse: record } : {})
          }, { merge: true });
        }
      } catch (err: any) {
        LoggerService.warn(`[FinancialLockService] Warning updating Firestore lock release for ${cleanPaymentId}:`, err.message || err);
      }
    }
  }

  /**
   * Deduplicate concurrent async calls in the same process via shared Promise execution.
   */
  static async executeWithLock<T>(
    paymentId: string,
    task: () => Promise<T>,
    adminDbInstance?: any
  ): Promise<T | { success: boolean; status: string; repasse?: any; message?: string }> {
    const cleanPaymentId = paymentId.trim();

    // Deduplicate same-process simultaneous execution
    if (activePromises.has(cleanPaymentId)) {
      const primaryResult = await activePromises.get(cleanPaymentId)!;
      if (primaryResult && typeof primaryResult === "object") {
        return {
          ...primaryResult,
          alreadyActivated: true,
          alreadyProcessed: true,
          message: "Processamento concorrente já atendido pela execução em andamento (idempotente).",
        };
      }
      return primaryResult;
    }

    const executionPromise = (async () => {
      const lock = await FinancialLockService.acquireLock(cleanPaymentId, adminDbInstance);
      if (!lock.acquired) {
        if (lock.reason === "ALREADY_PROCESSED") {
          return {
            success: true,
            status: "already_processed",
            repasse: lock.existingRecord
          };
        }
        if (lock.reason === "PROCESSING_IN_PROGRESS") {
          return {
            success: true,
            status: "already_processing",
            message: "Processamento financeiro já em andamento.",
            repasse: lock.existingRecord
          };
        }
      }

      try {
        const result = await task();
        const isSuccess = (result as any)?.success !== false;
        const finalStatus = (result as any)?.repasse?.status || (result as any)?.status || (isSuccess ? "CONCLUIDO" : "FALHO");
        await FinancialLockService.releaseLock(cleanPaymentId, finalStatus, (result as any)?.repasse || result, adminDbInstance);
        return result;
      } catch (err: any) {
        await FinancialLockService.releaseLock(cleanPaymentId, "FALHO", { errorMessage: err.message }, adminDbInstance);
        throw err;
      }
    })();

    activePromises.set(cleanPaymentId, executionPromise);
    try {
      return await executionPromise;
    } finally {
      activePromises.delete(cleanPaymentId);
    }
  }

  /**
   * Helper to inspect lock state
   */
  static getLockState(paymentId: string) {
    return inMemoryLocks[paymentId.trim()] || null;
  }

  /**
   * Check if lock is active for paymentId
   */
  static isLockActive(paymentId: string): boolean {
    const lock = this.getLockState(paymentId);
    return Boolean(lock && lock.status === "PROCESSING");
  }

  /**
   * Helper for testing/manual reset
   */
  static resetForTest() {
    activePromises.clear();
    inMemoryProcessedEvents.clear();
    Object.keys(inMemoryLocks).forEach((k) => delete inMemoryLocks[k]);
  }

  /**
   * Helper to manually set lock state (for testing timeouts)
   */
  static setLockStateForTest(paymentId: string, state: { status: string; lockedAt: string; processedAt?: string; record?: any }) {
    inMemoryLocks[paymentId.trim()] = state;
  }
}
